import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import {
  listMyLscClaimOpportunities,
  submitLscMemberClaim,
  LscApiError,
} from '../api';
import {
  claimOpportunityBadgeClass,
  claimOpportunityBadgeLabel,
  claimOpportunityBadgeStatus,
  claimOpportunityShowsOpenLeague,
  claimOpportunityShowsRequestButton,
  claimOpportunityShowsViewStatus,
  claimOpportunityStatusMessage,
  claimOpportunityViewStatusLabel,
  CLAIM_OPPORTUNITY_DISCORD_COPY,
  CLAIM_OPPORTUNITY_EMPTY_COPY,
  CLAIM_OPPORTUNITY_ERROR_COPY,
  CLAIM_OPPORTUNITY_LOADING_COPY,
  CLAIM_OPPORTUNITY_PANEL_INTRO,
  CLAIM_OPPORTUNITY_PANEL_TITLE,
  CLAIM_OPPORTUNITY_REJECTION_HEADING,
  formatClaimRejectionReason,
  formatClaimSourceProvider,
} from '../myClaimOpportunities';
import type { LscMyClaimOpportunity } from '../types';
import { lscPath } from '../routing';
import ErrorBanner from './ErrorBanner';
import LoadingState from './LoadingState';
import { useLeague } from '../hooks/useLeague';
import CfbInlineTeamChip from './CfbInlineTeamChip';

interface MyClaimOpportunitiesPanelProps {
  leagueId?: string;
  compactEmpty?: boolean;
}

const claimCtaClass =
  'inline-flex items-center justify-center min-h-11 px-4 text-sm rounded-lg transition-colors';

export function MyClaimOpportunityCard({
  item,
  onUpdated,
}: {
  item: LscMyClaimOpportunity;
  onUpdated?: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const badgeStatus = claimOpportunityBadgeStatus(item);
  const badgeLabel = claimOpportunityBadgeLabel(badgeStatus);
  const message = claimOpportunityStatusMessage(item);
  const showRequest = claimOpportunityShowsRequestButton(item);
  const showOpenLeague = claimOpportunityShowsOpenLeague(item);
  const showViewStatus = claimOpportunityShowsViewStatus(item);
  const rejectionReason = formatClaimRejectionReason(item.rejection_reason);
  const { league } = useLeague();
  const showCfbTeamChip =
    league?.game_key === 'cfb' && Boolean(item.current_side_label?.trim());

  const handleRequestClaim = async () => {
    if (!item.league_id || !item.candidate_member_id) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitLscMemberClaim(item.league_id, item.candidate_member_id);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not submit claim request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li
      className="card space-y-3 overflow-hidden"
      data-testid="my-claim-opportunity-card"
      data-claim-status={badgeStatus}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-white break-words">
            {item.league_name || 'League workspace'}
          </h3>
          {item.current_side_label && (
            <p className="text-sm text-secondary-300 mt-1">
              Team:{' '}
              {showCfbTeamChip ? (
                <CfbInlineTeamChip label={item.current_side_label} showLabel />
              ) : (
                item.current_side_label
              )}
            </p>
          )}
        </div>
        <span
          className={`inline-flex shrink-0 self-start items-center rounded-full border px-2.5 py-1 text-xs font-medium ${claimOpportunityBadgeClass(
            badgeStatus
          )}`}
          data-testid="claim-opportunity-badge"
        >
          {badgeLabel}
        </span>
      </div>

      {badgeStatus !== 'ambiguous' && (
        <div className="text-sm text-secondary-400 space-y-1">
          {item.in_game_name && <p>In-game name: {item.in_game_name}</p>}
          <p>Source: {formatClaimSourceProvider(item.source_provider)}</p>
        </div>
      )}

      <p className="text-sm text-secondary-300" data-testid="claim-opportunity-message">
        {message}
      </p>

      {rejectionReason && badgeStatus === 'rejected' && (
        <div
          className="rounded-lg border border-rose-800/40 bg-rose-950/20 px-3 py-2 space-y-1"
          data-testid="claim-opportunity-rejection-reason"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-rose-200/80">
            {CLAIM_OPPORTUNITY_REJECTION_HEADING}
          </p>
          <p className="text-sm text-rose-100/90">{rejectionReason}</p>
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {showRequest && (
          <button
            type="button"
            className={`${claimCtaClass} btn-primary`}
            disabled={submitting}
            onClick={() => void handleRequestClaim()}
            data-testid="request-claim-button"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Submitting…
              </span>
            ) : (
              'Request claim'
            )}
          </button>
        )}
        {showViewStatus && (
          <Link
            to={lscPath('/settings')}
            className={`${claimCtaClass} btn-secondary`}
            data-testid="view-claim-status-cta"
          >
            {claimOpportunityViewStatusLabel(item)}
          </Link>
        )}
        {showOpenLeague && item.league_id && (
          <Link
            to={lscPath(`/leagues/${item.league_id}`)}
            className={`${claimCtaClass} btn-secondary`}
            data-testid="open-league-cta"
          >
            Open league
          </Link>
        )}
      </div>
    </li>
  );
}

const MyClaimOpportunitiesPanel: React.FC<MyClaimOpportunitiesPanelProps> = ({
  leagueId,
  compactEmpty = false,
}) => {
  const { user, refreshUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discordConnected, setDiscordConnected] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<LscMyClaimOpportunity[]>([]);

  const loadOpportunities = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listMyLscClaimOpportunities();
      setDiscordConnected(Boolean(data.discord_connected));
      setMessage(data.message || null);
      setOpportunities(data.opportunities ?? []);
    } catch (err) {
      if (err instanceof LscApiError && err.status === 401) {
        await refreshUser();
      }
      setError(err instanceof LscApiError ? err.message : CLAIM_OPPORTUNITY_ERROR_COPY);
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, [refreshUser, user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    void loadOpportunities();
  }, [loadOpportunities, user?.uid]);

  const visibleOpportunities = useMemo(() => {
    if (!leagueId) return opportunities;
    return opportunities.filter((item) => item.league_id === leagueId);
  }, [leagueId, opportunities]);

  if (loading) {
    return (
      <div data-testid="my-claim-opportunities-loading">
        <LoadingState message={CLAIM_OPPORTUNITY_LOADING_COPY} />
      </div>
    );
  }

  if (!discordConnected) {
    if (compactEmpty) return null;
    return (
      <section className="card space-y-3" data-testid="my-claim-opportunities-panel">
        <h2 className="text-lg font-semibold text-white">{CLAIM_OPPORTUNITY_PANEL_TITLE}</h2>
        <p className="text-sm text-secondary-400">
          {message || CLAIM_OPPORTUNITY_DISCORD_COPY}
        </p>
        <Link
          to={lscPath('/settings')}
          className={`${claimCtaClass} btn-secondary self-start`}
          data-testid="claim-connect-discord-cta"
        >
          Connect Discord in settings
        </Link>
      </section>
    );
  }

  if (error && !visibleOpportunities.length) {
    if (compactEmpty) return null;
    return (
      <section className="card space-y-3" data-testid="my-claim-opportunities-panel">
        <h2 className="text-lg font-semibold text-white">{CLAIM_OPPORTUNITY_PANEL_TITLE}</h2>
        <ErrorBanner message={error} />
        <button
          type="button"
          className={`${claimCtaClass} btn-secondary self-start`}
          onClick={() => void loadOpportunities()}
          data-testid="claim-opportunities-retry"
        >
          Try again
        </button>
      </section>
    );
  }

  if (!visibleOpportunities.length) {
    if (compactEmpty) return null;
    return (
      <section className="card space-y-3" data-testid="my-claim-opportunities-panel">
        <h2 className="text-lg font-semibold text-white">{CLAIM_OPPORTUNITY_PANEL_TITLE}</h2>
        <p className="text-sm text-secondary-400" data-testid="claim-opportunities-empty">
          {message || CLAIM_OPPORTUNITY_EMPTY_COPY}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="my-claim-opportunities-panel">
      <div>
        <h2 className="text-lg font-semibold text-white">{CLAIM_OPPORTUNITY_PANEL_TITLE}</h2>
        <p className="text-sm text-secondary-400 mt-1">{CLAIM_OPPORTUNITY_PANEL_INTRO}</p>
      </div>

      {error && <ErrorBanner message={error} />}

      <ul className="space-y-3" data-testid="my-claim-opportunities-list">
        {visibleOpportunities.map((item) => (
          <MyClaimOpportunityCard
            key={`${item.league_id}-${item.candidate_member_id || item.claim_status}`}
            item={item}
            onUpdated={loadOpportunities}
          />
        ))}
      </ul>
    </section>
  );
};

export default MyClaimOpportunitiesPanel;
